import { useMemo } from "react";
import { Target, AlertTriangle, Phone, TrendingDown } from "lucide-react";
import Modal from "../components/Modal";
import { fmtShort, getToday } from "../utils";
import { progressoDoDia, diasEmDebito, metaDe } from "../lib/metas";

// ═══════════════════════════════════════════════════════════════
// COBRANÇA DE META — o dia anterior que não fechou
// ═══════════════════════════════════════════════════════════════
//
// Aparece na abertura quando existe dia útil fechado abaixo da meta. Cobra o
// que já passou, nunca o dia de hoje — cobrar às 9h um dia que ainda tem oito
// horas pela frente é ruído, e ruído a pessoa aprende a fechar sem ler.
//
// Não trava o sistema: mostra o buraco, oferece o caminho (registrar contato)
// e sai. Quem precisa cobrar de verdade é o gerente, não um pop-up.

export default function CobrancaMeta({ open, partners = [], metas = [], userId, hoje = getToday(), onClose, onRegistrar }) {
  const prog = useMemo(() => progressoDoDia(partners, userId, metas, hoje), [partners, metas, userId, hoje]);
  const debitos = useMemo(() => diasEmDebito(partners, userId, metas, hoje), [partners, metas, userId, hoje]);
  const meta = metaDe(metas, userId);

  if (!open || !debitos.length) return null;

  const total = debitos.reduce((s, d) => s + d.faltaram, 0);
  const zerados = debitos.filter((d) => d.feitos === 0).length;

  return (
    <Modal open={open} onClose={onClose} title="Meta de contatos" w={520} closeOnBackdrop={false}>
      <div style={{ display: "flex", gap: 9, padding: "12px 14px", borderRadius: 9, background: "#fef2f2", border: "1px solid #fecaca", marginBottom: 14 }}>
        <AlertTriangle size={17} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 12, color: "#991b1b", lineHeight: 1.5 }}>
          <strong>{debitos.length} {debitos.length === 1 ? "dia útil fechou" : "dias úteis fecharam"} abaixo da meta</strong> nos últimos 7.
          Somando, foram <strong>{total} {total === 1 ? "contato" : "contatos"}</strong> que não aconteceram.
          {zerados > 0 && <> Em {zerados} {zerados === 1 ? "deles não houve" : "deles não houve"} nenhum registro.</>}
        </div>
      </div>

      <div style={{ border: "1px solid #e5e7eb", borderRadius: 9, overflow: "hidden", marginBottom: 14 }}>
        {debitos.map((d, i) => (
          <div key={d.dia} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "9px 12px", borderTop: i ? "1px solid #f1f5f9" : "none", background: d.feitos === 0 ? "#fef2f2" : "#fff" }}>
            <span style={{ fontSize: 12, color: "#334155", fontWeight: 600 }}>{fmtShort(d.dia)}</span>
            <div style={{ flex: 1, height: 6, borderRadius: 4, background: "#f1f5f9", margin: "0 10px", overflow: "hidden" }}>
              <div style={{ width: `${Math.min(100, Math.round((d.feitos / d.meta) * 100))}%`, height: "100%", background: d.feitos === 0 ? "#dc2626" : "#d97706" }} />
            </div>
            <span style={{ fontSize: 11.5, color: d.feitos === 0 ? "#991b1b" : "#b45309", whiteSpace: "nowrap", fontWeight: 600 }}>
              {d.feitos} de {d.meta}
            </span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", borderRadius: 9, background: prog.bateu ? "#f0fdf4" : "#f8fafc", border: `1px solid ${prog.bateu ? "#bbf7d0" : "#e2e8f0"}`, marginBottom: 14 }}>
        {prog.bateu ? <Target size={16} color="#059669" /> : <TrendingDown size={16} color="#64748b" />}
        <div style={{ fontSize: 12, color: prog.bateu ? "#166534" : "#475569", lineHeight: 1.45 }}>
          <strong>Hoje: {prog.feitos} de {prog.meta}.</strong>{" "}
          {prog.bateu
            ? "Meta batida — o dia de hoje está resolvido."
            : `Ainda dá para fechar: faltam ${prog.faltam}.`}
          {meta.individual && <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 2 }}>Você tem meta individual definida pela gerência.</div>}
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer", marginRight: "auto" }}>
          Entendi
        </button>
        <button onClick={onRegistrar} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 16px", borderRadius: 7, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          <Phone size={13} /> Ver quem contatar
        </button>
      </div>
    </Modal>
  );
}
